import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  BRIEFING_ROW_SCOPE,
  fetchBriefingRows,
  type BriefingRow,
  type BriefingRowScope,
} from './api';

/**
 * Fetches briefing rows (campaigns scoped to the current user) and groups them
 * by plan_id for the accordion display.
 */
export function useBriefingRows(
  { platform, format }: BriefingRowScope = BRIEFING_ROW_SCOPE,
  {
    enabled = true,
    refetchOnMount = true,
  }: {
    /** Off where the rows would go unused, so nothing is fetched for them. */
    enabled?: boolean;
    /**
     * `'always'` where a stale answer would mislead: the app caches for five
     * minutes, and a row briefed in that time still reads as waiting.
     */
    refetchOnMount?: boolean | 'always';
  } = {},
) {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['asset-library', 'briefing-rows', { platform, format }],
    queryFn: () => fetchBriefingRows(platform, format),
    enabled,
    refetchOnMount,
  });

  const rows = useMemo(() => data ?? [], [data]);

  const groupedByPlan = useMemo(() => {
    const map = new Map<string, BriefingRow[]>();
    for (const row of rows) {
      const key = row.plan_id ?? 'no-plan';
      const group = map.get(key);
      if (group) {
        group.push(row);
      } else {
        map.set(key, [row]);
      }
    }
    return map;
  }, [rows]);

  return { rows, groupedByPlan, isLoading, isError, refetch };
}
